import { useMutation } from "@apollo/client";
import ReceiptReviewStatus from "enums/ReceiptReviewStatus";
import ViolationStatus from "enums/ViolationStatus";
import {
  APPROVE_MISMATCH_RECEIPT,
  MARK_LOST_RECEIPT_AS_APPROVED,
  MARK_RECEIPT_AS_LOST,
  REJECT_MISMATCH_RECEIPT,
} from "graphql/transaction";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { AttachmentList, ConfirmDialog } from "@outpave/ds";
import { useDisplayAttachments } from "hooks/useDisplayAttachments";
import { useReceiptAssets } from "hooks/useReceiptAssets";
import { MAX_RECEIPTS_PER_ENTITY } from "utils/receipts/receiptUpload";
import type { AttachmentSource } from "utils/attachments/attachmentItems";
import { toAttachmentItems } from "utils/attachments/attachmentItems";
import {
  useAttachmentListLabels,
  useAttachmentViewerLabels,
} from "hooks/useAttachmentLabels";
import { Box, Button, List } from "@mui/material";
import { RECEIPT_FILE_ACCEPT } from "utils/receipts/receiptFileTypes";
import Role from "enums/Role";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import TransactionsType from "enums/TransactionsType";

/**
 * Everything this component reads off a transaction, and nothing else.
 *
 * Written out because it was `any`, which is how the same component could be
 * rendered from admin and employee without anyone noticing the two pass
 * different shapes. Both satisfy this.
 */
export interface ReceiptRequirementTransaction {
  id?: string;
  type?: string;
  /** Server-computed per user: false for your own transaction, so this is
   *  also what keeps Approve Lost correct in /my/. */
  isLostApprovalAllowed?: boolean | null;
  policyStatus?: {
    receipt?: {
      violationStatus?: string | null;
      isExempt?: boolean | null;
    } | null;
  } | null;
  receiptDetail?: {
    id?: string;
    // AttachmentSource, not ImageAsset: employee's ReceiptAsset has a nullable
    // assetUrl and admin's does not. `any` hid that; the structural type is
    // what lets both satisfy one contract without either being widened.
    assets?: AttachmentSource[] | null;
  } | null;
}

export interface ImageAsset {
  assetUrl: string;
  id: string;
  extension?: string;
  parentId?: string;
  subParentId?: string;
  name?: string;
  description?: string;
  type?: string;
  size?: number;
  receiptDetailId?: string;
}

interface ReceiptReviewDetailsProps {
  transaction: ReceiptRequirementTransaction | null | undefined;
  handleRefreshTransactions?: () => void;
  refetchTransactionData: () => void;
  forceHideLostReceiptButton?: boolean;
}

export const ReceiptReviewDetails: React.FC<ReceiptReviewDetailsProps> = (
  props,
) => {
  const {
    transaction,
    handleRefreshTransactions,
    refetchTransactionData,
    forceHideLostReceiptButton = false,
  } = props;

  const { t } = useTranslation();
  const attachmentLabels = useAttachmentListLabels();
  const viewerLabels = useAttachmentViewerLabels();
  const canAdjudicateMismatch = useCheckUserRole(Role.Admin);

  const receiptReviewStatus = useMemo(() => {
    switch (transaction?.policyStatus?.receipt?.violationStatus) {
      case ViolationStatus.MATCH:
        return ReceiptReviewStatus.Match;
      case ViolationStatus.EXEMPT:
        return ReceiptReviewStatus.ExemptReceipt;
      case ViolationStatus.MISMATCH:
        return ReceiptReviewStatus.MismatchReceipt;
      case ViolationStatus.LOST:
        return ReceiptReviewStatus.LostReceipt;
      case ViolationStatus.APPROVED_LOST:
        return ReceiptReviewStatus.ApprovedLostReceipt;
      default:
        return ReceiptReviewStatus.MissingReceipt;
    }
  }, [transaction]);

  const [markReceiptAsLost] = useMutation(MARK_RECEIPT_AS_LOST);
  const [markLostReceiptAsApproved] = useMutation(
    MARK_LOST_RECEIPT_AS_APPROVED,
  );

  const [approveMismatchReceipt] = useMutation(APPROVE_MISMATCH_RECEIPT);
  const [rejectMismatchReceipt] = useMutation(REJECT_MISMATCH_RECEIPT);

  const [openReceiptLostModal, setOpenReceiptLostModal] = useState(false);
  const [openDeleteReceiptModal, setOpenDeleteReceiptModal] = useState(false);
  const [openApproveLostReceiptModal, setOpenApproveLostReceiptModal] =
    useState(false);

  const isReimbursement =
    transaction?.type === TransactionsType.MILEAGE ||
    transaction?.type === TransactionsType.CASH;

  const isReceiptExempt = transaction?.policyStatus?.receipt?.isExempt;
  const allReceiptAssets = transaction?.receiptDetail?.assets;
  const { printAsset, downloadAsset } = useDisplayAttachments({
    allAttachments: allReceiptAssets ?? [],
  });
  const receiptFiles = toAttachmentItems(allReceiptAssets ?? [], t("Receipt"));
  const {
    uploadReceipts,
    uploadingReceipts,
    pendingAssets,
    removeReceiptAsset,
    removingReceiptAsset,
  } = useReceiptAssets({
    target: { kind: "transaction", transactionId: transaction?.id ?? null },
    receiptId: transaction?.receiptDetail?.id,
    existingAssets: allReceiptAssets ?? [],
    onChanged: refetchTransactionData,
  });

  // `handleRefreshTransactions` re-derives the parent list's compliance, and
  // only the empty/non-empty flip changes it: the first upload and the last
  // removal. Counting before the call is what makes that knowable, since the
  // refetch has not landed yet.
  const handleAddReceipts = async (picked: File[]) => {
    const wasEmpty = (allReceiptAssets?.length ?? 0) === 0;
    const transfer = new DataTransfer();
    picked.forEach((file) => transfer.items.add(file));
    await uploadReceipts(transfer.files);
    if (wasEmpty) handleRefreshTransactions?.();
  };

  const handleRemoveAsset = async (assetId: string) => {
    const wasLast = (allReceiptAssets?.length ?? 0) === 1;
    const removed = await removeReceiptAsset(assetId);
    // Only on success: compliance has not changed if the delete failed.
    if (removed && wasLast) handleRefreshTransactions?.();
  };

  const pendingReceiptLabel = (status: string) =>
    status === "UPLOADING"
      ? t("Uploading")
      : status === "QUEUED"
        ? t("Queued")
        : t("Scanning");

  const showLostReceiptButton =
    receiptReviewStatus === ReceiptReviewStatus.MissingReceipt &&
    pendingAssets.length === 0 &&
    !isReceiptExempt &&
    !forceHideLostReceiptButton &&
    !isReimbursement;
  const showApproveLostReceiptButton =
    receiptReviewStatus === ReceiptReviewStatus.LostReceipt &&
    !forceHideLostReceiptButton &&
    transaction?.isLostApprovalAllowed;

  const isShowApprovalMismatchReceiptButtons = useMemo(() => {
    return (
      transaction?.policyStatus?.receipt?.violationStatus ===
        ViolationStatus.MISMATCH &&
      !transaction?.policyStatus?.receipt?.isExempt &&
      canAdjudicateMismatch
    );
  }, [transaction?.policyStatus?.receipt, canAdjudicateMismatch]);

  const [assetIdToDelete, setAssetIdToDelete] = useState<string | null>(null);

  const handleDeleteAssetModal = (assetId: string) => {
    setOpenDeleteReceiptModal(true);
    setAssetIdToDelete(assetId);
  };

  const markReceiptAsLostFunct = async () => {
    try {
      await markReceiptAsLost({
        variables: {
          transactionId: transaction?.id,
        },
      });
      toast.success(t("Receipt marked as lost successfully"));
      if (handleRefreshTransactions) handleRefreshTransactions();
      refetchTransactionData();
    } catch (error) {
      toast.error(t("Unable to mark receipt as lost"));
      console.error("Unable to mark receipt as lost.", error);
    }
  };

  const markLostReciptAsApprovedFunct = async () => {
    try {
      await markLostReceiptAsApproved({
        variables: {
          transactionId: transaction?.id,
        },
      });
      toast.success(t("Lost receipt marked as approved successfully"));
      if (handleRefreshTransactions) handleRefreshTransactions();
      refetchTransactionData();
    } catch (error) {
      toast.error(t("Unable to mark lost receipt as approved"));
      console.error("Unable to mark lost receipt as approved.", error);
    }
  };

  const handleApproveMismatchReceiptClick = async () => {
    try {
      await approveMismatchReceipt({
        variables: {
          transactionId: transaction?.id,
        },
      });
      toast.success(t("Approved mismatch receipt successfully"));
      if (handleRefreshTransactions) handleRefreshTransactions();
      refetchTransactionData();
    } catch (error) {
      toast.error(t("Unable to approve mismatch receipt"));
      console.error("Unable to approve mismatch receipt.", error);
    }
  };

  const handleRejectMismatchReceiptClick = async () => {
    try {
      await rejectMismatchReceipt({
        variables: {
          transactionId: transaction?.id,
        },
      });
      toast.success(t("Rejected mismatch receipt successfully"));
      if (handleRefreshTransactions) handleRefreshTransactions();
      refetchTransactionData();
    } catch (error) {
      toast.error(t("Unable to reject mismatch receipt"));
      console.error("Unable to reject mismatch receipt.", error);
    }
  };

  const deleteReceipt = async () => {
    if (assetIdToDelete) await handleRemoveAsset(assetIdToDelete);
  };

  return (
    <>
      <List>
        <AttachmentList
          files={[
            ...receiptFiles,
            ...toAttachmentItems(pendingAssets, t("Receipt")).map(
              (item, index) => ({
                ...item,
                pending: pendingReceiptLabel(
                  pendingAssets[index].processingStatus,
                ),
              }),
            ),
          ]}
          accept={RECEIPT_FILE_ACCEPT}
          multiple
          maxFiles={MAX_RECEIPTS_PER_ENTITY}
          hint={
            uploadingReceipts ? t("Uploading receipts") : t("FiveReceiptUpload")
          }
          labels={attachmentLabels}
          viewer={{ labels: viewerLabels, filmstrip: true }}
          onAdd={handleAddReceipts}
          onDownload={(item) => item.url && downloadAsset(item.url)}
          onPrint={(item) => item.url && printAsset(item.url)}
          disabled={uploadingReceipts || removingReceiptAsset}
          onRemove={handleDeleteAssetModal}
        />
        {(!allReceiptAssets || allReceiptAssets?.length < 5) && (
          <>
            <Box
              sx={{
                display: "flex",
                gap: "var(--space-4)",
                justifyContent: "flex-start",
              }}
            >
              {showLostReceiptButton && (
                <Button
                  variant="danger-outlined"
                  size="medium"
                  style={{ marginRight: ".5rem" }}
                  fullWidth
                  onClick={(event) => {
                    event.stopPropagation();
                    setOpenReceiptLostModal(true);
                  }}
                >
                  {t("Mark as Lost")}
                </Button>
              )}
              {showApproveLostReceiptButton && (
                <Button
                  variant="secondary-filled"
                  size="medium"
                  fullWidth
                  style={{ marginRight: ".5rem" }}
                  onClick={() => {
                    setOpenApproveLostReceiptModal(true);
                  }}
                >
                  {t("Approve Lost")}
                </Button>
              )}
              {isShowApprovalMismatchReceiptButtons && (
                <>
                  <Button
                    variant="primary-filled"
                    onClick={handleApproveMismatchReceiptClick}
                  >
                    {t("Approve")}
                  </Button>
                  <Button
                    variant="danger-outlined"
                    onClick={handleRejectMismatchReceiptClick}
                  >
                    {t("Reject")}
                  </Button>
                </>
              )}
            </Box>
          </>
        )}
      </List>

      <ConfirmDialog
        open={openDeleteReceiptModal}
        title={t("Remove Receipt")}
        message={t("Are you sure you want to remove this receipt?")}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        onConfirm={() => {
          deleteReceipt();
          setOpenDeleteReceiptModal(false);
        }}
        onCancel={() => setOpenDeleteReceiptModal(false)}
      />
      <ConfirmDialog
        open={openReceiptLostModal}
        title={t("Mark as Lost")}
        message={t("Are you sure you want to mark this receipt as lost?")}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        onConfirm={() => {
          markReceiptAsLostFunct();
          setOpenReceiptLostModal(false);
        }}
        onCancel={() => setOpenReceiptLostModal(false)}
      />
      <ConfirmDialog
        open={openApproveLostReceiptModal}
        title={t("Approve Lost Receipt?")}
        message={t("ApproveLostReceipt")}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        tone="primary"
        onConfirm={() => {
          markLostReciptAsApprovedFunct();
          setOpenApproveLostReceiptModal(false);
        }}
        onCancel={() => setOpenApproveLostReceiptModal(false)}
      />
    </>
  );
};
